import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CustomInput from "../../custom/custom-input/CustomInput";
import CustomButton from "../../custom/custom-button/CustomButton";
import InsurancePhoneCloseIcon from "../../shared/icons/InsurancePhoneCloseIcon";
import userSessionData from "../../../services/userSessionData";
import styles from "./InsurancePhoneModal.module.scss";

export default function InsurancePhoneModal({ onClose, onSaved }) {
  const { t } = useTranslation();
  const userId = useSelector((state) => state.user.id);
  const sessionId = useSelector((state) => state.authSession.sessionId);
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const active = useRef(false);
  const dialog = useRef(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    active.current = true;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.querySelector("input")?.focus();
    return () => {
      active.current = false;
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  useEffect(() => {
    if (saving) dialog.current?.focus();
  }, [saving]);

  const close = () => {
    if (!submitting.current) onClose();
  };
  const handleKeys = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
    if (event.key !== "Tab") return;
    const focusable = dialog.current.querySelectorAll("button:not(:disabled), input:not(:disabled)");
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first) {
      event.preventDefault();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const save = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!/^7\d{10}$/.test(phone.replace(/\D/g, "")) || phone.includes("_")) {
      setError(t("profileTwo.validation.invalidPhone"));
      return;
    }
    submitting.current = true;
    setSaving(true);
    setError("");
    try {
      const saved = await userSessionData.updatePhone({
        phoneNumber: phone.trim(), expectedUserId: userId, expectedSessionId: sessionId,
      });
      if (active.current && saved) onSaved();
    } catch {
      if (active.current) setError(t("cargoInsurance.phoneModal.saveError"));
    } finally {
      submitting.current = false;
      if (active.current) setSaving(false);
    }
  };

  return createPortal(
    <div className={styles.overlay} onClick={close}>
      <form ref={dialog} className={styles.modal} role="dialog" aria-modal="true" tabIndex={-1}
        aria-labelledby={titleId} aria-describedby={descriptionId} aria-busy={saving}
        onClick={(event) => event.stopPropagation()} onKeyDown={handleKeys} onSubmit={save}>
        <div className={styles.intro}>
          <div className={styles.header}>
            <h2 id={titleId}>{t("cargoInsurance.phoneModal.title")}</h2>
            <button className={styles.close} type="button" onClick={close} disabled={saving}
              aria-label={t("common.close")}><InsurancePhoneCloseIcon /></button>
          </div>
          <p id={descriptionId}>{t("cargoInsurance.phoneModal.description")}</p>
        </div>
        <div className={styles.field}>
          <CustomInput isPhone isBgGray value={phone} isDisabled={saving}
            labelText={t("cargoInsurance.phoneModal.label")} placeholder="+7 7777777777"
            onChangeValue={(value) => { setPhone(value); setError(""); }} />
          {error && <p className={styles.error} role="alert">{error}</p>}
        </div>
        <CustomButton type="submit" isFall isLoading={saving} isDisabled={saving} disabled={saving}
          text={t("common.save")} />
      </form>
    </div>, document.body,
  );
}
